import "./service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";
import Icon from "../../components/Icon";
import useReveal from "../../hooks/useReveal";

const offerings = [
  {
    icon: "layers",
    title: "Custom Generative AI Applications",
    desc: "Tailored GenAI applications designed specifically for your domain, workflows, and operational needs. We build scalable AI platforms instead of generic chatbot templates.",
  },
  {
    icon: "search",
    title: "RAG (Retrieval-Augmented Generation) Systems",
    desc: "Build intelligent AI systems connected to your business data, documents, databases, and internal knowledge sources for accurate, context-aware responses.",
  },
  {
    icon: "chat",
    title: "AI Chatbots & Virtual Assistants",
    desc: "Deploy intelligent AI assistants capable of customer support, internal operations, task automation, and business process handling.",
  },
  {
    icon: "code",
    title: "AI Code Generation & Developer Tools",
    desc: "AI-powered engineering tools that generate, review, optimize, and automate development workflows to improve productivity and software quality.",
  },
  {
    icon: "document",
    title: "Document AI & Knowledge Extraction",
    desc: "Extract structured intelligence from PDFs, CSV files, contracts, invoices, reports, manuals, and enterprise documents.",
  },
  {
    icon: "search",
    title: "AI-Powered Search & Semantic Discovery",
    desc: "Create advanced search systems that understand meaning, user intent, and contextual relevance across enterprise datasets.",
  },
  {
    icon: "workflow",
    title: "Workflow Automation using LangGraph",
    desc: "Design stateful, multi-agent AI workflows for business automation, approvals, operations management, and intelligent decision systems.",
  },
];

const techGroups = [
  { label: "Core Technologies", items: ["Python", "FastAPI", "Node.js", "TypeScript", "Go"] },
  { label: "AI / LLM Frameworks", items: ["LangChain", "LangGraph", "OpenAI APIs", "Anthropic Claude", "Hugging Face", "Ollama"] },
  { label: "Vector Databases", items: ["Pinecone", "ChromaDB", "FAISS", "Weaviate", "Qdrant"] },
  { label: "Cloud & Infrastructure", items: ["AWS", "Docker", "Kubernetes", "Vercel", "Firebase", "Supabase"] },
  { label: "Frontend Technologies", items: ["React.js", "Next.js", "React Native", "Tailwind CSS", "Expo"] },
];

const useCases = [
  {
    icon: "chat",
    title: "Smart Customer Support Systems",
    desc: "AI-powered support agents that resolve customer queries instantly, automate ticket handling, reduce operational costs, and improve customer satisfaction.",
  },
  {
    icon: "document",
    title: "AI Document Intelligence Platforms",
    desc: "Upload contracts, reports, invoices, compliance documents, or manuals and extract structured insights, summaries, risks, and business intelligence instantly.",
  },
  {
    icon: "code",
    title: "AI Coding & Engineering Assistants",
    desc: "Custom AI copilots that help engineering teams generate code, review architecture, automate testing, and accelerate development workflows.",
  },
  {
    icon: "target",
    title: "Business Process Automation Tools",
    desc: "End-to-end AI automation systems integrated into enterprise operations for approvals, routing, scheduling, reporting, and intelligent workflow execution.",
  },
];

export default function AIApplication() {
  const [offeringsRef, offeringsVisible] = useReveal();
  const [useCasesRef, useCasesVisible] = useReveal();

  return (
    <>
      <SEO
        title="AI App Development: GenAI, LLM & RAG | Best Code Technology"
        description="We build AI applications using GenAI, LLM, RAG and automation technologies for scalable business solutions."
        keywords="AI application development, GenAI, LLM, RAG, AI agents, machine learning"
        url="https://bestcodetechnology.in/services/ai-applications"
      />
      <div className="svc-page">
        {/* ── Hero ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <div className="svc-hero__eyebrow">
                  <span className="pill-label">AI-Powered Enterprise Solutions</span>
                </div>
                <h1 className="svc-hero__headline">
                  Build AI Products That <span>Scale With Your Business</span>
                </h1>
                <p className="svc-hero__desc">
                  We design and develop production-ready AI systems for
                  startups, enterprises, and modern digital platforms. From
                  intelligent assistants and RAG pipelines to workflow
                  automation and AI-powered business platforms, we transform
                  business operations into scalable AI ecosystems.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── What We Offer ── */}
        <section className="svc-offerings">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <h2 className="svc-section-title">
                  AI Engineering
                </h2>
                <p className="svc-section-sub">
                  We provide end-to-end AI engineering services built with
                  scalable architecture, enterprise-grade security, and modern
                  LLM ecosystems.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={offeringsRef}>
              {offerings.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-md-6 col-xl-4 reveal ${
                    offeringsVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <div
                      className="svc-offer-card__divider"
                      aria-hidden="true"
                    />
                    <h4 className="svc-offer-card__title">{item.title}</h4>
                    <p className="svc-offer-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tech Stack ── */}
        <section className="svc-tech">
          <div className="container-xl">
            <div className="row align-items-start g-5">
              <div className="col-12 col-lg-4 svc-tech__col-left pe-lg-5">
                <h2 className="svc-section-title">Our AI Technology Stack</h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Battle-tested technologies selected for scalability,
                  performance, reliability, and enterprise AI deployment.
                </p>
              </div>

              <div className="col-12 col-lg-8">
                <div className="svc-tech__groups">
                  {techGroups.map((group) => (
                    <div key={group.label} className="svc-tech__group">
                      <span className="svc-tech__group-label">
                        {group.label}
                      </span>
                      <div className="svc-tech__badges">
                        {group.items.map((tech) => (
                          <span key={tech} className="svc-tech__badge">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Use Cases ── */}
        <section className="svc-usecases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <h2 className="svc-section-title">
                  Real Problems.{" "}
                  <span className="svc-gradient-text">Real AI Solutions.</span>
                </h2>
                <p className="svc-section-sub">
                  We build practical AI systems that solve operational
                  challenges, automate workflows, improve customer experiences,
                  and accelerate business growth.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={useCasesRef}>
              {useCases.map((uc, i) => (
                <div
                  key={uc.title}
                  className={`col-12 col-lg-6 reveal ${
                    useCasesVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-usecase-card h-100">
                    <div className="svc-usecase-card__top">
                      <div className="svc-usecase-card__icon">
                        <Icon name={uc.icon} size={22} />
                      </div>
                    </div>
                    <h3 className="svc-usecase-card__title">{uc.title}</h3>
                    <p className="svc-usecase-card__desc">{uc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
